/**
 * **Le logement** — ⟦A-11 §1 et §2⟧, l'écran des charges et de la porte de sortie.
 *
 * ## Pourquoi cet écran existe
 *
 * Mesuré et consigné dans l'avenant : couvrir 5 ₵/jour demande **16 667 vues/jour en shorts**
 * contre **1 111 en longues** (RPM 0,3 contre 4,5). L'économie est calibrée pour un joueur qui
 * publie des LONGUES — et celui qui vient d'atteindre 1 000 abonnés publie encore des shorts.
 * *« Rien ne l'enseigne, et c'est précisément le trou de l'arc 3. »* Ce panneau est l'endroit
 * où on le lui dit, avec ses propres chiffres.
 *
 * ## La sortie n'est pas un seuil, c'est une décision
 *
 * Les charges ne se coupent **jamais** toutes seules : des charges qui s'arrêtent d'elles-mêmes
 * feraient cesser le saignement avant que le joueur puisse mourir, et la fin « faillite »
 * deviendrait inatteignable. Le déséquilibre est le **prix de l'existence de la faillite**,
 * pas un bug. La porte de sortie, c'est `retour_parents` — une décision, avec son prix.
 *
 * **Et le prix, c'est le studio.** Rentrer rabat le plafond de compétence au socle
 * (`PLAFOND_CHEZ_LES_PARENTS`) : on ne tourne pas la même vidéo dans une chambre d'enfant.
 * D'où l'auto-équilibrage d'A-11 — une grosse chaîne qui rentrerait saborderait sa production
 * pour économiser 5 ₵/jour. Aucun garde-fou artificiel à écrire, et il n'y en a aucun ici.
 *
 * Tous les chiffres viennent du moteur (`chargesDuJour`, `actionCost`, `RPM_*`,
 * `plafondCompetence`) : ils sont ✎ et bougeront en M3.12.
 *
 * ## ⟦C57 · lot 5a · A-174⟧ Moins de texte : les chiffres à l'écran, la leçon derrière « Les dessous »
 *
 * Mesuré avant le chantier : 165 mots visibles, dont une phrase de 25 mots. À l'écran : les charges
 * par jour, le plafond, les DEUX chiffres de la leçon (les vues par jour qu'il faut en shorts et en
 * longues), et le bouton de la porte de sortie avec sa phrase courte. Tout le reste, **mot pour
 * mot**, est dans un seul « Les dessous » : la leçon des shorts, la porte de sortie, la faillite qui
 * approche. Les deux confirmations tiennent en deux phrases courtes (ce que ça coûte, ce que ça
 * change). Rien n'est supprimé, aucun chiffre n'est recopié : tout vient toujours du moteur.
 */

import {
  CHARGES_FIXES_PAR_JOUR,
  COUT_REPRENDRE_STUDIO,
  H_FAILLITE,
  H_JOUR,
  PLAFOND_CHEZ_LES_PARENTS,
  RPM_LONGUE,
  RPM_SHORT,
  actionCost,
  chargesDuJour,
  estMonetise,
  plafondCompetence,
  type Action,
} from "@maxime/shared";

import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";
import { jourSimule, libelleCout, nombre } from "../../game/format";
import { useRunStore } from "../../stores/runStore";
import { dialog } from "../../stores/uiStore";

export function Logement() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);

  if (!run) return null;
  const state = run.state;
  const c = contenu();
  const chezLesParents = state.flags.chez_les_parents === true;
  const charges = chargesDuJour(state);
  const cout = (action: Action) => libelleCout(actionCost(action, state));

  // Le plafond d'écriture sert de témoin : c'est celui qui bouge quand on perd le studio.
  const plafondActuel = plafondCompetence(state, "ecriture", c);

  // ⟦A-11 §1⟧ Les deux vérités du RPM, calculées et pas racontées : voilà combien de vues
  // par jour il faut, dans chaque format, pour couvrir ce que la vie coûte.
  const vuesPourCouvrir = (rpm: number) => (charges > 0 ? Math.ceil((charges / rpm) * 1000) : 0);

  // Jours restants avant la faillite, si le compteur du moteur tourne (§11.5).
  const depuis = state.counters.money_negative_since_h;
  const joursAvantFaillite =
    depuis === null ? null : Math.max(0, Math.ceil((H_FAILLITE - (state.clock_h - depuis)) / H_JOUR));

  async function rentrer() {
    const ok = await dialog.confirm({
      title: "Retourner chez tes parents ?",
      // ⟦C57 · A-174⟧ Deux phrases courtes : ce que ça change, ce que ça coûtera de repartir. Le détail
      // (« sur toutes les branches », « la chambre se vide de son matériel ») est dans les dessous.
      body: `Plus de loyer, mais plafond ${plafondActuel} → ${PLAFOND_CHEZ_LES_PARENTS} et chambre vidée. Re-partir : ${nombre(COUT_REPRENDRE_STUDIO)} ₵.`,
      confirmLabel: "Je remonte les cartons",
      cancelLabel: "Je tiens encore",
      danger: true,
    });
    if (ok) await act({ type: "retour_parents" });
  }

  async function repartir() {
    const ok = await dialog.confirm({
      title: "Reprendre un studio ?",
      body: `${nombre(COUT_REPRENDRE_STUDIO)} ₵ d'un coup, puis ${nombre(CHARGES_FIXES_PAR_JOUR)} ₵ de loyer par jour. Ton plafond de compétence remonte.`,
      confirmLabel: "Je reprends un studio",
      cancelLabel: "Encore un peu ici",
    });
    if (ok) await act({ type: "reprendre_studio" });
  }

  const monetise = estMonetise(state);
  const rpmShort = String(RPM_SHORT).replace(".", ",");
  const rpmLongue = String(RPM_LONGUE).replace(".", ",");

  return (
    <Panneau titre={`🏠 Logement — ${nombre(state.money)} ₵`}>
      {/* ⟦C57 · A-174⟧ Une phrase de chiffres, et UN « Les dessous » pour tout ce que l'écran disait en plus. */}
      <p className="ligne-info l5-entete">
        <span>
          {chezLesParents ? (
            <>
              Chez tes parents : <strong>0 ₵</strong> par jour.
            </>
          ) : (
            <>
              Ton studio : <strong>{nombre(charges)} ₵</strong> par jour, publié ou non.
            </>
          )}
        </span>
        <LesDessous titre="Comment marche le logement">
          <DessousBloc titre="Tes charges">
            <p className="dessous-texte">
              {chezLesParents
                ? "Tu es chez tes parents. Zéro charge, et la chambre d'enfant qui va avec."
                : `Tu as ton studio. Il te coûte ${nombre(charges)} ₵ par jour, tous les jours, que tu publies ou non.`}
            </p>
            <p className="dessous-texte">
              Jour {jourSimule(state.clock_h)} · plafond de compétence actuel {plafondActuel}
              {chezLesParents ? " (socle sans studio)" : ""} ·{" "}
              {monetise ? "chaîne monétisée" : "chaîne non monétisée"}
            </p>
          </DessousBloc>

          {joursAvantFaillite !== null ? (
            <DessousBloc titre="💸 Dans le rouge">
              <p className="dessous-texte">
                Tu es dans le rouge. Un mois dans cet état et c'est la faillite : il te reste{" "}
                <strong>{joursAvantFaillite} jour(s)</strong>.
              </p>
            </DessousBloc>
          ) : null}

          {/* ⟦A-11 §1⟧ La leçon de l'arc 3, avec SES chiffres à lui. */}
          <DessousBloc titre="💡 Ce que personne ne t'a dit sur les shorts">
            <p className="dessous-texte">
              Les shorts ne monétisent presque pas : {rpmShort} ₵ pour 1 000 vues, contre {rpmLongue}{" "}
              ₵ pour une longue — quinze fois moins.
            </p>
            {charges > 0 ? (
              <p className="dessous-texte">
                Pour couvrir tes {nombre(charges)} ₵ de charges quotidiennes, il te faut{" "}
                <strong>{nombre(vuesPourCouvrir(RPM_SHORT))} vues/jour</strong> en shorts… ou{" "}
                <strong>{nombre(vuesPourCouvrir(RPM_LONGUE))} vues/jour</strong> en longues. Ce n'est
                pas la même chaîne.
              </p>
            ) : null}
            <p className="dessous-texte">
              Ce n'est pas un déséquilibre à corriger : c'est le métier. Les longues paient, les
              shorts font venir.
            </p>
          </DessousBloc>

          {chezLesParents ? (
            <DessousBloc titre="📦 Reprendre un studio">
              <p className="dessous-texte">
                Le temps d'apprendre à faire des longues est passé. Reprendre un lieu à toi remet le
                loyer sur la table — et remet ton plafond de compétence en jeu : sans studio, il
                reste bloqué à {PLAFOND_CHEZ_LES_PARENTS}, quoi que tu saches faire.
              </p>
            </DessousBloc>
          ) : (
            <DessousBloc titre="🎒 Retourner chez tes parents">
              <p className="dessous-texte">
                La porte de sortie, et elle a un prix. Les charges s'arrêtent net — mais tu tournes
                dans une chambre d'enfant : le plafond de compétence retombe à{" "}
                {PLAFOND_CHEZ_LES_PARENTS} sur toutes les branches. C'est du temps pour apprendre à
                faire des longues, pas une astuce d'optimisation : à grande échelle, économiser{" "}
                {nombre(charges)} ₵/jour en sabordant ses vidéos est perdant.
              </p>
              <p className="dessous-texte">
                Retourner chez tes parents : les charges s'arrêtent, tu perds le studio, et la
                chambre se vide de son matériel. Re-partir te coûtera {nombre(COUT_REPRENDRE_STUDIO)}{" "}
                ₵.
              </p>
            </DessousBloc>
          )}
        </LesDessous>
      </p>
      <p className="ligne-info discret">
        Jour {jourSimule(state.clock_h)} · plafond de compétence {plafondActuel} ·{" "}
        {monetise ? "chaîne monétisée" : "chaîne non monétisée"}
      </p>

      {joursAvantFaillite !== null ? (
        <p className="bandeau-sm">
          <span>
            💸 Dans le rouge : faillite dans <strong>{joursAvantFaillite} jour(s)</strong>.
          </span>
        </p>
      ) : null}

      {/* ⟦A-11 §1⟧ La leçon de l'arc 3, avec SES chiffres à lui : deux nombres, pas un paragraphe. */}
      <section className="carte">
        <h2 className="bloc-titre">💡 Shorts ou longues ?</h2>
        <p className="ligne-info">
          Pour 1 000 vues : <strong>{rpmShort} ₵</strong> en short, <strong>{rpmLongue} ₵</strong> en
          longue.
        </p>
        {charges > 0 ? (
          <p className="ligne-info">
            Charges : {nombre(charges)} ₵/jour = <strong>{nombre(vuesPourCouvrir(RPM_SHORT))}</strong> vues
            en short, <strong>{nombre(vuesPourCouvrir(RPM_LONGUE))}</strong> en longue.
          </p>
        ) : null}
      </section>

      {chezLesParents ? (
        <section className="carte">
          <h2 className="bloc-titre">📦 Reprendre un studio</h2>
          <p className="ligne-info">
            Loyer à {nombre(CHARGES_FIXES_PAR_JOUR)} ₵ par jour, plafond de compétence libéré.
          </p>
          <button
            className="bouton primaire"
            type="button"
            disabled={pending || state.money < COUT_REPRENDRE_STUDIO}
            onClick={() => void repartir()}
          >
            Reprendre un studio
            <span className="cout">{cout({ type: "reprendre_studio" })}</span>
          </button>
          {state.money < COUT_REPRENDRE_STUDIO ? (
            <p className="ligne-info discret">Il te manque {nombre(COUT_REPRENDRE_STUDIO - state.money)} ₵.</p>
          ) : null}
        </section>
      ) : (
        <section className="carte">
          <h2 className="bloc-titre">🎒 Retourner chez tes parents</h2>
          <p className="ligne-info">
            Charges à zéro, mais plafond de compétence à {PLAFOND_CHEZ_LES_PARENTS}.
          </p>
          <button className="bouton" type="button" disabled={pending} onClick={() => void rentrer()}>
            Remonter les cartons
            <span className="cout">{cout({ type: "retour_parents" })}</span>
          </button>
        </section>
      )}
    </Panneau>
  );
}
